import { useEffect, useState } from 'react';
import { ComplianceChecklist } from '../components/ComplianceChecklist';
import { accessLevelLabel, gitlabApi, type GitlabMeStatus } from '../lib/gitlab-api';
import { GitBranch } from 'lucide-react';

/**
 * OnboardingPage — "My Account → Onboarding" (docs/specs §B, T-E).
 *
 * Card #1: Connect your GitLab. Reads GET /api/gitlab/me for current state and
 * starts the OAuth flow via GET /api/gitlab/oauth/start (→ redirect to GitLab).
 * The OAuth return is handled by GitlabCallbackPage at /onboarding/gitlab/cb.
 *
 * Future cards (SuperAdmin, VPN, copilots) are out of scope — the page shell
 * leaves room for them.
 */
export function OnboardingPage() {
  const [me, setMe] = useState<GitlabMeStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    gitlabApi
      .me()
      .then(setMe)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  async function connect() {
    setError(null);
    setRedirecting(true);
    try {
      const { authorizeUrl } = await gitlabApi.start();
      window.location.href = authorizeUrl;
    } catch (e) {
      setError((e as Error).message);
      setRedirecting(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-8" data-testid="onboarding-page">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <GitBranch className="h-6 w-6" />
          {'Onboarding'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {'Get set up at OptiSigns. Connect your accounts below — start with GitLab so you can pull repos and open merge requests.'}
        </p>
      </header>

      <section className="rounded-lg border bg-card p-6" data-testid="gitlab-card">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-foreground">{'Connect your GitLab'}</h2>
            <p className="text-sm text-muted-foreground">
              {'Sign in to GitLab with your @optisigns.com Google account (with 2FA enabled). We grant you Developer access to the optisigns group automatically.'}
            </p>
          </div>
          <StatusBadge loading={loading} me={me} />
        </div>

        <div className="mt-5">
          {loading ? (
            <p className="text-sm text-muted-foreground">{'Checking status…'}</p>
          ) : me?.connected ? (
            <ConnectedView me={me} />
          ) : me?.offboardedAt ? (
            <p className="text-sm text-muted-foreground" data-testid="gitlab-offboarded">
              {'Your GitLab access was removed (account deactivated). Reconnect if this is a mistake.'}
            </p>
          ) : (
            <button
              type="button"
              onClick={connect}
              disabled={redirecting}
              data-testid="gitlab-connect-btn"
              className="inline-flex items-center rounded-md bg-foreground px-4 py-2 text-sm font-medium text-background disabled:opacity-50"
            >
              {redirecting ? 'Redirecting…' : 'Connect your GitLab'}
            </button>
          )}

          {error ? (
            <p
              className="mt-3 rounded-md border border-destructive/40 bg-destructive-soft px-3 py-2 text-sm text-destructive"
              data-testid="gitlab-error"
            >
              {error}
            </p>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function StatusBadge({ loading, me }: { loading: boolean; me: GitlabMeStatus | null }) {
  if (loading) return null;
  const connected = me?.connected;
  return (
    <span
      className="inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium text-foreground"
      data-testid="gitlab-status-badge"
    >
      <span className={`h-2 w-2 rounded-full ${connected ? 'bg-success' : 'bg-muted-foreground'}`} />
      {connected ? 'Connected' : 'Not connected'}
    </span>
  );
}

function ConnectedView({ me }: { me: GitlabMeStatus }) {
  const level = accessLevelLabel(me.accessLevel);
  return (
    <div className="space-y-3 text-sm" data-testid="gitlab-connected">
      <p className="text-foreground">
        {'Connected as '}
        <span className="font-mono font-medium">{`@${me.gitlabUsername}`}</span>
        {level ? ` — ${level} on the optisigns group.` : ' — access on the optisigns group.'}
      </p>
      {/* All gates passed at connect — render the checklist all-green. */}
      <ComplianceChecklist />
    </div>
  );
}
